// SETTINGS V2 — location row (SETTINGS_V2_SP4 §5.2/§5.3). Parent/child row card:
// left expander, pin icon, name + Predvolená badge, mono subtitle, badges,
// Upraviť button, Radix kebab menu. Parent rows are bordered cards with shadow;
// child rows same minus shadow, indented. Inline styles + scoped CSS-var tokens +
// Radix dropdown. No MUI/dt/Tailwind. SEC-REQ-2: no side effects.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import type { Location } from "../../../machinery/components/locations/locationTypes";
import { SettingsV2Tooltip } from "../atoms";
import LocationRowV2Menu from "./LocationRowV2Menu";

const ellipsis: React.CSSProperties = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export interface LocationRowV2Props {
  location: Location;
  isChild?: boolean;
  expanded?: boolean;
  onToggleExpand?: () => void;
  onEdit: (location: Location) => void;
  onSetDefault?: (location: Location) => void;
  onDisable: (location: Location) => void;
  onDelete?: (location: Location) => void;
  busy?: boolean;
  // MAP-A-FE2 (§5.6, additive): root-div scroll anchor id (map pin-click targets
  // `sv2-loc-${id}`) + flash flag applying the `.sv2-loc-flash` pulse to the root
  // (a child's indented root is the correct target; a full-width wrapper misaligns).
  domId?: string;
  flashing?: boolean;
  // §enhancement: pin-icon click focuses the map (inert when no coords); `selected`
  // tints the icon red to mirror the selected map pin.
  onPinIconClick?: (location: Location) => void;
  selected?: boolean;
}

const LocationRowV2: React.FC<LocationRowV2Props> = ({
  location,
  isChild = false,
  expanded,
  onToggleExpand,
  onEdit,
  onSetDefault,
  onDisable,
  onDelete,
  busy,
  domId,
  flashing,
  onPinIconClick,
  selected,
}) => {
  const { translate: t } = useTranslation("settings");
  const { translate: tCommon } = useTranslation("common");
  const { isPhone } = useBreakpoint();

  const childCount = location.childLocations?.length ?? 0;
  const hasChildren = childCount > 0;
  const hasCoords = location.latitude != null && location.longitude != null;

  // Subtitle join — verbatim from legacy LocationRow (parity; description /
  // countryName are dead in practice but the expression is parity-bound).
  const subtitle =
    location.description ||
    [location.addressLine1, location.zipCode, location.city, location.countryName]
      .filter(Boolean)
      .join(", ");

  return (
    <div
      id={domId}
      className={flashing ? "sv2-loc-flash" : undefined}
      style={{
        display: "flex",
        alignItems: "center",
        gap: isPhone ? 8 : 12,
        padding: isPhone ? "10px 12px" : "12px 16px",
        background: "var(--card)",
        border: "1px solid var(--border)",
        borderRadius: 10,
        boxShadow: isChild ? "none" : "var(--shadow)",
        marginLeft: isChild ? (isPhone ? 20 : 40) : 0,
        minWidth: 0,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* Left expander (parent only): button when it has children, spacer otherwise. */}
      {!isChild &&
        (hasChildren ? (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onToggleExpand}
            aria-expanded={expanded}
            aria-label={t("settings:locations.subLocations", { count: childCount })}
            style={{
              width: isPhone ? 44 : 28,
              minWidth: isPhone ? 44 : 28,
              height: isPhone ? 44 : 28,
              padding: 0,
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <svg
              viewBox="0 0 12 12"
              width="12"
              height="12"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
                transition: "transform 0.18s ease",
              }}
            >
              <path d="M3 4.5 L6 7.5 L9 4.5" />
            </svg>
          </button>
        ) : (
          <span style={{ width: isPhone ? 8 : 28, flexShrink: 0 }} aria-hidden="true" />
        ))}

      {/* Pin icon. §enhancement: located → button that focuses the map (selected →
          red-tinted to mirror the map pin); unlocated → inert + tooltip. */}
      {(() => {
        const iconBase: React.CSSProperties = {
          width: 34, height: 34, borderRadius: 9, flexShrink: 0,
          display: "grid", placeItems: "center",
          background: isChild ? "var(--bg-soft)" : "var(--mod-accent-soft)",
          border: `1px solid ${isChild ? "var(--border)" : "var(--mod-accent-line)"}`,
          color: isChild ? "var(--muted)" : "var(--mod-accent)",
        };
        const iconSelected: React.CSSProperties = {
          background: "color-mix(in oklab, var(--sv2-pin-sel) 12%, var(--card))",
          border: "1px solid var(--sv2-pin-sel)",
          color: "var(--sv2-pin-sel)",
        };
        const glyph = (
          <svg
            width="15"
            height="15"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M8 14.2 C8 14.2 3 9.8 3 6.3 A5 5 0 0 1 13 6.3 C13 9.8 8 14.2 8 14.2 Z" />
            <circle cx="8" cy="6.3" r="1.7" />
          </svg>
        );
        return hasCoords ? (
          <button
            type="button"
            className="sv2-locpin"
            onClick={() => onPinIconClick?.(location)}
            aria-label={t("settings:locations.focusOnMap")}
            style={{ ...iconBase, ...(selected ? iconSelected : null), padding: 0, cursor: "pointer" }}
          >
            {glyph}
          </button>
        ) : (
          <SettingsV2Tooltip text={t("settings:locations.noCoordsTooltip")} side="right">
            <span style={{ ...iconBase, cursor: "default" }}>{glyph}</span>
          </SettingsV2Tooltip>
        );
      })()}

      {/* Name + subtitle. */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)", minWidth: 0, ...ellipsis }}>
            {location.name}
          </span>
          {location.isDefault && !isPhone && (
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 8.5,
                fontWeight: 600,
                letterSpacing: "0.07em",
                textTransform: "uppercase",
                color: "var(--mod-accent)",
                background: "var(--mod-accent-soft)",
                border: "1px solid var(--mod-accent-line)",
                borderRadius: 999,
                padding: "2px 8px",
                flexShrink: 0,
                whiteSpace: "nowrap",
              }}
            >
              {t("settings:locations.defaultBadge")}
            </span>
          )}
          {/* Deactivated badge (SP4.3 Fix 2) — default-badge pill shape but red,
              so a disabled location reads as OFF. Shown on parent + child at ALL
              breakpoints; both badges render if default AND disabled. */}
          {location.isDisabled && (
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 8.5,
                fontWeight: 600,
                letterSpacing: "0.07em",
                textTransform: "uppercase",
                color: "var(--red)",
                background: "color-mix(in oklab, var(--red) 10%, var(--card))",
                border: "1px solid color-mix(in oklab, var(--red) 24%, transparent)",
                borderRadius: 999,
                padding: "2px 8px",
                flexShrink: 0,
                whiteSpace: "nowrap",
              }}
            >
              {t("settings:locations.deactivatedBadge")}
            </span>
          )}
        </div>
        {/* Address subtitle — ALWAYS visible when non-empty (SP4.1); wraps long
            addresses instead of ellipsis. Parent + child. */}
        {subtitle && (
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10.5,
              color: "var(--muted)",
              marginTop: 2,
              overflowWrap: "anywhere",
            }}
          >
            {subtitle}
          </div>
        )}
      </div>

      {/* Machines badge — dead path in prod (machineCount always undefined), kept for parity. */}
      {location.machineCount != null && location.machineCount > 0 && (
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--faint)",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {t("settings:locations.machines", { count: location.machineCount })}
        </span>
      )}

      {/* Sub-count text (parent w/ children; hidden on phone). */}
      {!isChild && hasChildren && !isPhone && (
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--muted)",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {t("settings:locations.subLocations", { count: childCount })}
        </span>
      )}

      {/* Upraviť. */}
      <button
        type="button"
        className="btn btn-ghost"
        onClick={() => onEdit(location)}
        style={{ height: isPhone ? 44 : 28, padding: "0 9px", whiteSpace: "nowrap", flexShrink: 0 }}
      >
        {tCommon("buttons.edit")}
      </button>

      {/* Kebab menu (§5.3) — set-default / Deaktivovať / Aktivovať / delete. */}
      <LocationRowV2Menu
        location={location}
        isChild={isChild}
        onSetDefault={onSetDefault}
        onDisable={onDisable}
        onDelete={onDelete}
        busy={busy}
      />
    </div>
  );
};

export default LocationRowV2;
